import { Button } from "azure-devops-ui/Button";
import {
  IObservableArray,
  IObservableArrayEventArgs,
  IReadonlyObservableArray,
  IReadonlyObservableValue,
  ObservableArray
} from "azure-devops-ui/Core/Observable";
import { FocusZone, FocusZoneDirection } from "azure-devops-ui/FocusZone";
import { IconSize } from "azure-devops-ui/Icon";
import { IMenuItem, MenuButton } from "azure-devops-ui/Menu";
import { Spinner, SpinnerSize } from "azure-devops-ui/Spinner";
import { css, getSafeId, getSafeIdSelector } from "azure-devops-ui/Util";
import React from "react";
import { unstable_batchedUpdates } from "react-dom";
import { lazy } from "../../common/components/lazy/lazy";
import { Observer } from "../../common/components/observer/observer";
import { ResponsiveLayout } from "../../common/components/responsivelayout/responsivelayout";
import { Scenario } from "../../common/components/scenario/scenario";
import { EventContext } from "../../common/contexts/event";
import { FeatureContext } from "../../common/contexts/feature";
import { NavigationContext } from "../../common/contexts/navigation";
import { useFocusCapture } from "../../common/hooks/usefocuscapture";
import { useEventListener } from "../../common/hooks/uselistener";
import { useObjectURL } from "../../common/hooks/useobjecturl";
import { useObservable, useSubscription, useSubscriptionArray } from "../../common/hooks/useobservable";
import { usePhotoZoom } from "../../common/hooks/usephotozoom";
import { useResize } from "../../common/hooks/useresize";
import { useTimeout } from "../../common/hooks/usetimeout";
import { useVideoSource } from "../../common/hooks/usevideosource";
import { supportsMediaSource } from "../../common/utilities/browser";
import { getFocusElements } from "../../common/utilities/focus";
import { format } from "../../common/utilities/format";
import { preventDefault } from "../../common/utilities/func";
import { ISelection, Selection } from "../../common/utilities/selection";
import { getItem as getDriveItem } from "../api/item";
import {
  IDownloadOptions,
  defaultPhotoSelect,
  downloadPhoto,
  getFavorites,
  getOldestPhoto,
  getPhoto,
  getPhotos,
  getPhotosPage,
  getRecentPlaces,
  getTagItems
} from "../api/photo";
import { getRecommendedItems } from "../api/recommendation";
import * as Icons from "../components/illustration/icons";
import { Motion } from "../components/illustration/icons";
import { Photo } from "../components/photo/photo";
import { TileOverlay } from "../components/tileoverlay/tileoverlay";
import { IVideoControls, Video, VideoControlsVisibility } from "../components/video/video";
import { AuthorizationContext } from "../contexts/authorization";
import { EmbedContext } from "../contexts/embed";
import { FeedContext } from "../contexts/feed";
import { SessionContext } from "../contexts/session";
import { useAlbumApi } from "../hooks/usealbumapi";
import { useFetch } from "../hooks/usefetch";
import { useLayoutStyle } from "../hooks/uselayoutstyle";
import { IPhotoContentOptions, PhotoContentScale, getPhotoContent } from "../hooks/usephotocontent";
import { usePhotoFeed } from "../hooks/usephotofeed";
import { useRejection } from "../hooks/userejection";
import { addToAlbum, createAlbum } from "../operations/album";
import { addFavorite, addTag, deletePhotos, deleteTag, downloadPhotos, loadPhotoDetails, removeFavorite, updatePhoto } from "../operations/photo";
import { IPhotoCreatedEvent, IPhotoUpdatedEvent, IPhotosDeletedEvent } from "../types/change";
import { IDimensions, IItemPage } from "../types/item";
import { IItemFeed } from "../types/itemfeed";
import { IRectangle } from "../types/layout";
import { IDetectedEntity, IPhoto } from "../types/photo";
import { IViewProps } from "../types/view";
import { filterAlbumsAuto } from "../utilities/albums";
import { computeCropRect, fitToDimensions, getPhotoTakenDate, getViewRoute, isSimilarPhoto } from "../utilities/image";
import { fullscreenSupported } from "../utilities/layout";
import { getMotionVideo, photoEditable } from "../utilities/photo";
import { getAllSelectedPhotos, getDuplicatedPhotosParent } from "../utilities/util";

import "./photoview.css";

const AddToAlbumPanel = lazy(() => import("../dialogs/addtoalbumdialog"));
const CreateAlbumDialog = lazy(() => import("../dialogs/createalbumdialog"));
const DeletePhotoDialog = lazy(() => import("../dialogs/deletephotodialog"));
const Details = lazy(() => import("../components/details/details"));
const EditDialog = lazy(() => import("../dialogs/editdialog"));
const OnePlayer = lazy(() => import("../components/video/oneplayer"));
const ShareDialog = lazy(() => import("../dialogs/sharedialogodc"));

const { MotionLabel, LiveLabel, Favorite, RemoveFavorite } = window.Resources.Common;

const {
  AddToAlbumButton,
  ClearSelectionButton,
  CloseButton,
  DeleteButton,
  DownloadButton,
  EditButton,
  HidePhotoDetails,
  MoreButton,
  SelectedItems,
  ShareButton,
  ShowPhotoDetails,
  VideoNotSupported
} = window.Resources.Common;
const { LoadFailureMessage, MorePhotos, NextButton, PreviousButton, ToggleFullScreen } = window.Resources.PhotoView;

const thumbnailHeight = 68;

interface IIndexRange {
  end: number;
  start: number;
}

interface IActiveItems {
  getItem: (id: string) => IPhoto | undefined;
  items: IObservableArray<IPhoto>;
}

export interface IPhotoViewProps extends IViewProps {
  /**
   * Id of the primary photo of the photo view.
   */
  id: string;

  /**
   * When the view is created from an existing view, the view can supply an
   * existing feed. This help optimize the loading of the photos since it can
   * share the photos from the parent feed. If one is not supplied a new
   * feed is built from the view parameters.
   */
  feedId?: number;

  /**
   * if navigated to photoview, the source of the navigation
   */
  navigationSource?: string;

  /**
   * Set of photos to initialize the view with.
   */
  photos?: IPhoto[];

  /**
   * Optional search string used to address this view.
   */
  search?: string;

  /**
   * Optional attribute to mark the photo as shared
   */
  shared?: boolean;
}

export function PhotoView(props: IPhotoViewProps): React.ReactElement {
  const { id, feedId = 0, navigationSource, photos, search, shared, setDocumentTitle, setDragDropOptions: setUploadOptions } = props;

  const eventContext = React.useContext(EventContext);
  const featureContext = React.useContext(FeatureContext);
  const feedContext = React.useContext(FeedContext);
  const navigationContext = React.useContext(NavigationContext);
  const sessionContext = React.useContext(SessionContext);

  const [, setCount] = React.useState(0);

  const [activeItems, setActiveItems] = React.useState<IActiveItems>(() => {
    const items = new ObservableArray<IPhoto>([]);

    // If we have initial photos we will fill in our items and map.
    if (photos) {
      items.push(...photos);
    }

    return { getItem: (id: string) => items.value.find((photo) => photo.id === id), items };
  });

  const [fallback, setFallback] = React.useState(navigationContext.prepare("/"));
  const [groupRange, setGroupRange] = useObservable<IIndexRange>({ end: 0, start: 0 });
  const [initialized, setInitialized] = React.useState(false);
  const [dataLoaded, setDataLoaded] = React.useState(false);
  const [selection] = React.useState(new Selection({ multiSelect: true }));

  // Extract the values from the URL needed to drive the page.
  const [{ extras, viewSource, viewToken }] = React.useState(() => {
    // Parse the search string for extra photos to be rendered in the viwe/group.
    const params = new URLSearchParams(search);
    const extras = params.getAll("id");
    const view = params.get("view") || undefined;
    const fallback = params.get("fallback") || undefined;

    // Determine the source of the photos. This controls how the photos
    // are retrieved. The source is based on the view that was used to open
    // this photo view. This is important for the scrolling behavior.
    const [viewSource, ...rest] = view ? view.split(",") : [];
    const viewToken = rest.join(",");

    // Setup the fallback URL's for these views if we opened the viewer directly.
    fallback && setFallback(fallback);

    return { extras, viewSource, viewToken };
  });

  // Create an error handler for reporting errors to the application.
  const handleRejection = useRejection();

  // Get the album api based on migration state.
  const albumApi = useAlbumApi();

  // Do we want to show photos in groups.
  const autoGroupPhotos = useSubscription(featureContext.featureEnabled("autoGroupPhotos"));

  // Look in the feedContext for a cached version of the feed. This will give us
  // all the data from the original view without us having to load it again.
  const [cachedFeed] = React.useState(() => feedContext.get<IPhoto>(feedId));

  // Network calls used to initialize the id/extras up front.
  const _getPhoto = useFetch(getPhoto, { name: "getPhoto" });
  const _getPhotoExtra = useFetch(getPhoto, { blocking: false, name: "getPhotoExtra" });

  // Network call for loading photos when a feed was not supplied.
  const _getAlbumPhotos = useFetch(albumApi.getAlbumPhotos, { blocking: false, name: "getAlbumPhotos" });
  const _getFavorites = useFetch(getFavorites, { name: "getFavorites" });
  const _getPhotos = useFetch(getPhotos, { blocking: false, name: "getPhotos" });
  const _getTagItems = useFetch(getTagItems, { blocking: false, name: "getTags" });
  const _getPlaceItems = useFetch(getRecentPlaces, { blocking: false, name: "getPlaces" });
  const _getPhotosPage = useFetch(getPhotosPage, { blocking: false, name: "getPhotosPage" });
  const _getRecommendedItems = useFetch(getRecommendedItems, { blocking: false, name: "getRecommendedItems" });

  const getItems = React.useCallback(
    (url?: string): Promise<IItemPage<IPhoto>> => {
      // If a page URL was supplied all sources use the same getItems API call.
      if (url) {
        return _getPhotosPage(url);
      }

      if (viewSource === "all") {
        // We need to the first photo shown, in UTC time.
        const dateTaken = getPhotoTakenDate(activeItems.items.value[0]);

        // Now that we have finished loading the requested photos, we need to go
        // ahead and pre-load the next/previous pages. Add 1 second to start
        // getting photos from just before the initial photo.
        return _getPhotos(sessionContext.driveId, {
          initialPageDateTaken: new Date(dateTaken.getTime() - dateTaken.getTimezoneOffset() * 60 * 1000 + 1000)
        });
      }

      if (viewSource === "album") {
        return _getAlbumPhotos(sessionContext.driveId, viewToken);
      }

      if (viewSource === "moment") {
        return _getRecommendedItems(sessionContext.driveId, viewToken);
      }

      if (viewSource === "tag") {
        return _getTagItems(sessionContext.driveId, viewToken);
      }

      if (viewSource === "place") {
        return _getPlaceItems(sessionContext.driveId, JSON.parse(viewToken));
      }

      if (viewSource === "favorites") {
        return _getFavorites();
      }

      // If no view is supplied we will just load any of the extras that were requested.
      const loadPromises: Promise<IPhoto>[] = [];

      // If additional photos were supplied we will load them as well.
      if (extras.length) {
        const existingPhotos = new Set(photos?.map((photo) => photo.id));
        for (let photoIndex = 0; photoIndex < extras.length; photoIndex++) {
          if (!existingPhotos.has(extras[photoIndex])) {
            loadPromises.push(_getPhotoExtra(sessionContext.driveId, extras[photoIndex]));
          }
        }
      }

      return Promise.allSettled(loadPromises).then((results) => {
        const overallPhotos: IPhoto[] = [...activeItems.items.value];

        // Save each of the photos into the initialItems.
        for (let photoIndex = 0; photoIndex < results.length; photoIndex++) {
          const result = results[photoIndex];
          if (result.status === "fulfilled") {
            overallPhotos.push(result.value);
          } else {
            // @TODO: Handle photos failing to load.
          }
        }

        return Promise.resolve<IItemPage<IPhoto>>({ value: overallPhotos });
      });
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_getAlbumPhotos, _getPhotos, _getPhotosPage, _getRecommendedItems, viewSource, viewToken]
  );

  // Setup our item feed in case the photo feed wasn't supplied.
  const _itemFeed = usePhotoFeed(getItems);

  // Use the cacheFeed if one was avilable, otherwise we can use the local feed.
  const itemFeed = cachedFeed || _itemFeed;

  React.useState(() => {
    if (!photos) {
      // Load the initial photo represented by the primary id, we will wait
      // for the feed to load the rest of the group.
      const select = defaultPhotoSelect + ",accessViewpoint";
      _getPhoto(sessionContext.driveId, id, { select })
        .then((photo) => {
          unstable_batchedUpdates(() => {
            activeItems.items.push(photo);

            completeInitialization();
          });
        })
        .catch((error) => {
          unstable_batchedUpdates(() => {
            // Mark initialization complete regardless after logging as needed
            setDataLoaded(true);
            setInitialized(true);

            // Show the error when we fail to load the photo.
            handleRejection(error);
          });
        });
    } else {
      completeInitialization();
    }

    function completeInitialization(): void {
      const initialPhoto = activeItems.items.value[0];

      // Update the selected group with the initial photos.
      if (autoGroupPhotos) {
        setGroupRange({ end: activeItems.items.length - 1, start: 0 });
      } else {
        setGroupRange({ end: 0, start: 0 });
      }

      selection.select(initialPhoto.id);

      // Make sure our feed contains the initial photos and we are ready to
      // transition away from the from the initialItems to the feed items.
      initializeFeed()
        .then(() => {
          unstable_batchedUpdates(() => {
            const { getItem, items } = itemFeed;

            // Update the group range, this should include the first item and all grouped items.
            for (let itemIndex = 0; itemIndex < items.value.length; itemIndex++) {
              const item = items.value[itemIndex];

              if (item.id === initialPhoto.id) {
                let endIndex = itemIndex;
                if (autoGroupPhotos) {
                  while (endIndex < items.value.length - 1 && isSimilarPhoto(items.value[endIndex], items.value[endIndex + 1])) {
                    endIndex++;
                  }
                }

                setGroupRange({ start: itemIndex, end: endIndex });
                break;
              }
            }

            // Update our rendered items to use the feed items instead of the initial items.
            setActiveItems({ getItem, items });
            setDataLoaded(true);
          });
        })
        .catch((error) => {
          setDataLoaded(true);
          handleRejection(error);
        });
    }

    function initializeFeed(): Promise<void> {
      if (cachedFeed && cachedFeed.items.value.find((item) => item.id === id)) {
        return Promise.resolve();
      }

      return _itemFeed
        .getItemsFromFeed()
        .then((photoPage) => {
          // If we start in the middle of the feed go ahead and fetch the previous
          // page to allow fast back navigation.
          if (photoPage.previousPage) {
            return _itemFeed.getItemsFromFeed(photoPage.previousPage);
          }
        })
        .then(ensureItemsLoaded);
    }

    function ensureItemsLoaded(): Promise<void> {
      // Continue to read the next page while we haven't read our initial items
      // and our feed has more items.
      if (_itemFeed.items.value.find((item) => item.id === id)) {
        return Promise.resolve();
      }

      if (_itemFeed.cursor.nextPage) {
        return _itemFeed.getItemsFromFeed(_itemFeed.cursor.nextPage).then(ensureItemsLoaded);
      }

      return Promise.reject(new Error(LoadFailureMessage));
    }
  });

  // When items are retrieved and placed in the item feed we need to update the
  // groupRange based on the shift in indexes.
  useSubscriptionArray(itemFeed.items, (changes: IObservableArrayEventArgs<IPhoto>) => {
    if (initialized) {
      const { addedItems, index, removedItems } = changes;
      let { end, start } = groupRange.value;

      // If items were added we want to update the group range with the inserted
      // items when they were inserted before our current group.
      if (addedItems && addedItems.length && index <= start) {
        end += addedItems.length;
        start += addedItems.length;
      }

      // If items were removed we need to update the selection and the group
      // range based on where the changes occurred.
      if (removedItems && removedItems.length && index <= end) {
        const inRangeCount = Math.min(end - index + 1, removedItems.length);
        const preRangeCount = start - Math.min(start, index);

        end -= inRangeCount + preRangeCount;
        start -= preRangeCount;

        // Unselect any items that were removed.
        for (let itemIndex = 0; itemIndex < removedItems.length; itemIndex++) {
          selection.unselect(removedItems[itemIndex].id);
        }

        // If all items from the current group are deleted navigate back.
        if (end < start) {
          navigationContext.navigate(undefined, "navigateBackButton", { fallback, useBack: true, useReplace: true });
          return;
        } else {
          // If nothing is selected select the first item in the group.
          if (selection.selectedCount === 0) {
            selection.select(itemFeed.items.value[start].id);
          }
        }
      }

      setGroupRange({ end, start });
    }
  });

  // We don't want Drag & Drop upload to work while in the OneUp.
  setUploadOptions({ disabled: true });

  // Setup our operation listeners to keep our current view up to date.
  React.useEffect(() => {
    eventContext.addEventListener("dataChanged", dataChanged);

    return () => {
      eventContext.removeEventListener("dataChanged", dataChanged);
    };

    function dataChanged(event: IPhotoCreatedEvent | IPhotosDeletedEvent | IPhotoUpdatedEvent) {
      if (event.changeType === "photoCreated") {
        const photo = event.data.photo;

        if (!selection.selected(photo.id)) {
          window.history.replaceState({ feedId, photos: [photo] }, "", getViewRoute([photo], viewSource, viewToken, fallback));
          selection.select(photo.id);
        }

        setCount((count) => count + 1);
      } else if (event.changeType === "photosDeleted") {
        selection.unselect(event.data.photoIds);
      } else if (event.changeType === "photoUpdated") {
        setCount((count) => count + 1);
      }
    }
  }, [eventContext, feedId, fallback, feedContext, groupRange, itemFeed.items, navigationContext, selection, viewToken, viewSource]);

  return (
    <Scenario complete={() => initialized} scenarioName="renderPhoto">
      {dataLoaded ? (
        <PhotoView2
          autoGroupPhotos={autoGroupPhotos}
          fallback={fallback}
          feedId={feedId}
          getItem={activeItems.getItem}
          groupRange={groupRange}
          itemFeed={itemFeed}
          items={activeItems.items}
          navigationSource={navigationSource}
          selection={selection}
          setDocumentTitle={setDocumentTitle}
          setGroupRange={setGroupRange}
          setPhotoViewLoaded={setInitialized}
          shared={shared}
          viewSource={viewSource}
          viewToken={viewToken}
        />
      ) : (
        <div className="photo-view standard-view flex-column flex-grow" data-theme={sessionContext.embedded ? undefined : "dark"}>
          <div className="photo-view-content flex-grow" />
        </div>
      )}
    </Scenario>
  );
}

interface IPhotoView2Props {
  /**
   * Should photos be grouped when they are shown.
   */
  autoGroupPhotos: boolean;

  /**
   * When the view is closed what URL should be used. If this is in the local
   * history it will move back to that page.
   */
  fallback: string;

  /**
   * The identifer for the feed we are currently using.
   */
  feedId?: number;

  /**
   * Function to get a photo from the source.
   *
   * @param id Id of the photo to retrieve
   * @returns The matching photo or undefined
   */
  getItem: (id: string) => IPhoto | undefined;

  /**
   * Range of photos by index to render.
   */
  groupRange: IReadonlyObservableValue<IIndexRange>;

  /**
   * The caller will supply an item feed which we will use to scroll through
   * the items in the view we came from.
   */
  itemFeed: IItemFeed<IPhoto>;

  /**
   * Set of available items.
   */
  items: IReadonlyObservableArray<IPhoto>;

  /**
   * Source if the page got navigated to photoview
   */
  navigationSource?: string;

  /**
   * The current selection of photos.
   */
  selection: ISelection;

  /**
   * Used to setup the current page title for this view.
   *
   * @param title Title string used to describe the views contents.
   */
  setDocumentTitle: (title: string) => void;

  /**
   * setPhotoViewLoaded is used to let the parent component know that
   * the photo is downloaded and ready to render
   */
  setPhotoViewLoaded: React.Dispatch<React.SetStateAction<boolean>>;

  /**
   * setGroupRange can be used to update the set of photos that should be shown.
   */
  setGroupRange: React.Dispatch<React.SetStateAction<IIndexRange>>;

  /**
   * Optional attribute to mark item as shared.
   */
  shared?: boolean;

  /**
   * View these photos were sourced from. Examples: "album", "all" or "moment"
   */
  viewSource?: string;

  /**
   * View instance these photos were sourced from. Example albumId or moment key.
   */
  viewToken?: string;
}

function PhotoView2(props: IPhotoView2Props): React.ReactElement {
  const {
    autoGroupPhotos,
    fallback,
    feedId,
    getItem,
    itemFeed,
    navigationSource,
    selection,
    setDocumentTitle,
    setGroupRange,
    setPhotoViewLoaded,
    shared,
    viewSource,
    viewToken
  } = props;
  const { cursor, getItemsFromFeed } = itemFeed;

  const authorizationContext = React.useContext(AuthorizationContext);
  const embedContext = React.useContext(EmbedContext);
  const eventContext = React.useContext(EventContext);
  const featureContext = React.useContext(FeatureContext);
  const navigationContext = React.useContext(NavigationContext);
  const sessionContext = React.useContext(SessionContext);

  const containerElement = React.useRef<HTMLDivElement>(null);
  const detailsButton = React.useRef<Button>(null);
  const imageReferenceElement = React.useRef<HTMLDivElement>(null);
  const playing = React.useRef<boolean>(false);
  const titlebarElement = React.useRef<HTMLDivElement>(null);
  const titlebarRightElement = React.useRef<HTMLDivElement>(null);
  const videoPlayer = React.useRef<IVideoControls>();
  const viewElement = React.useRef<HTMLDivElement>(null);
  const videoElement = React.useRef<HTMLVideoElement>(null);
  const zoomContainerElement = React.useRef<HTMLDivElement>(null);

  const [, setCount] = React.useState(0);
  const [readyToDraw, setReadyToDraw] = React.useState(false);
  const [contentUrl, setContentUrl] = useObservable("");
  const [loaded, setLoaded] = useObservable(false);
  const [scale, setScale] = useObservable<PhotoContentScale>(undefined);
  const [showAddToAlbumDialog, setShowAddToAlbumDialog] = useObservable(false);
  const [showCreateAlbumDialog, setShowCreateAlbumDialog] = useObservable<boolean>(false);
  const [showControls, setShowControls] = useObservable<VideoControlsVisibility>("invisible");
  const [showDeleteDialog, setShowDeleteDialog] = useObservable(false);
  const [showDetails, setShowDetails] = useObservable(false);
  const [showFullscreen, setShowFullscreen] = React.useState(fullscreenSupported() && !!document.fullscreenElement);
  const [showMore, setShowMore] = useObservable(false);
  const [showShareDialog, setShowShareDialog] = useObservable(false);
  const [showEdit, setShowEdit] = useObservable(false);
  const [showVideo, setShowVideo] = useObservable(false);
  const [motionVideoUrl, setMotionVideoUrl] = useObservable("");
  const [transition, setTransition] = useObservable<boolean | undefined>(undefined);
  const [transitionType, setTransitionType] = useObservable<"pause" | "play" | undefined>(undefined);

  const enableServerZipDownload = useSubscription(featureContext.featureEnabled("EnableServerZipDownload"));
  const showAlbums = useSubscription(featureContext.featureEnabled("showAlbums"));
  const useOnePlayer = useSubscription(featureContext.featureEnabled("useOnePlayer"));

  // Need to re-render when the container changes size.
  useResize(containerElement, () => setCount((count) => count + 1));

  // Track the full screen state changes.
  useEventListener(document, "fullscreenchange", () => {
    const fullscreenElement = fullscreenSupported() && !!document.fullscreenElement;
    setShowFullscreen(fullscreenElement);
  });

  // Create an error handler for reporting errors to the application.
  const handleRejection = useRejection();
  const handleRejectionWarning = useRejection({ messageType: "warning" });

  // Get the album api based on migration state.
  const albumApi = useAlbumApi();

  // Setup focus capture for our view.
  const { onBlur, onFocus } = useFocusCapture(getSafeIdSelector("photo-view-container"));

  // Track the layout style we are using in the view.
  const layoutStyle = useLayoutStyle();

  // We will re-render when the item list, group range, selection changes.
  const items = useSubscriptionArray(props.items);
  const groupRange = useSubscription(props.groupRange);
  useSubscription(selection);

  const { clearTimeout: clearClickTimeout, setTimeout: setClickTimeout } = useTimeout();
  const { setTimeout: setControlsTimeout } = useTimeout();
  const { setTimeout: setMoreTimeout } = useTimeout();
  const { setTimeout: setTransitionTimeout } = useTimeout();

  // When we re-render with a new active photo set we need to ensure focus is
  // within the view. If we were previously in the strip and the strip is still
  // available keep focus in the strip.
  React.useEffect(() => {
    if (!viewElement.current!.contains(document.activeElement)) {
      const focusElements = getFocusElements(viewElement.current!, true);
      focusElements.find((focusElement) => focusElement.classList.contains("photo-view-content"))?.focus();
    }
  }, [groupRange.end, groupRange.start]);

  // We can't configure the callout's to be dark mode through normal code we
  // will use a mutation observer to add that data-theme attribute to the
  // callout content element. This will enforce dark-mode theme in the callout.
  // istanbul ignore next - not going to write tests to test all the mutation cases.
  React.useEffect(() => {
    if (!sessionContext.embedded) {
      const portalHost = document.querySelector<HTMLElement>(".bolt-portal-host");
      if (portalHost) {
        const observer = new MutationObserver((mutations) => {
          for (let mutationIndex = 0; mutationIndex < mutations.length; mutationIndex++) {
            const mutation = mutations[mutationIndex];
            if (mutation.addedNodes.length) {
              const newElement = mutation.addedNodes[0] as HTMLElement;
              const calloutContent = newElement.querySelector(".bolt-callout-content");
              if (calloutContent) {
                calloutContent.setAttribute("data-theme", "dark");
              }
            }
          }
        });

        observer.observe(portalHost, { childList: true });

        return () => {
          observer.disconnect();
        };
      }
    }
  }, [sessionContext.embedded]);

  const _getPhotoContent = (photoId: string, options?: IPhotoContentOptions | undefined, preview?: boolean) => {
    const getContent = getPhotoContent(downloadPhoto);
    const photoContent = getContent(photoId, options);

    const url = useSubscription(photoContent.url);

    // dont update the url while photo component is rendering - push it back on the queue
    setTimeout(() => {
      selection.selectedCount === 1 && url && !preview && setContentUrl(url);
    });

    return photoContent;
  };

  const clientRect = containerElement.current ? containerElement.current.getBoundingClientRect() : undefined;
  const columnCount = Math.max(1, Math.min(clientRect ? Math.floor(clientRect.width / 400) : 5, selection.selectedCount));
  const columnWidth = clientRect
    ? (Math.floor(clientRect.width) - 8 * (columnCount + (selection.selectedCount > 1 ? 4 : 0) - 1)) / columnCount
    : undefined;
  const style = { width: selection.selectedCount > 1 ? columnWidth : undefined };

  // Build the set of rendered photos from the ones currently selected.
  const renderedPhotos: React.ReactElement[] = [];
  const supportsMediaSrc = supportsMediaSource();

  let selectionDimensions: IDimensions | undefined = undefined;

  if (clientRect) {
    selection.value.forEach((id) => {
      const photo = getItem(id)!;
      const dimensions = fitToDimensions(
        clientRect.height - (selection.selectedCount > 1 ? 16 : 0),
        Math.min(columnWidth!, photo.dimensions.width),
        photo.dimensions.height,
        photo.dimensions.width
      );

      selectionDimensions = dimensions;

      renderedPhotos.push(
        <div
          className="photo-view-photo flex-row flex-align-center flex-justify-center overflow-hidden relative"
          key={photo.id}
          ref={imageReferenceElement}
          style={style}
        >
          {photo.video ? (
            useOnePlayer ? (
              <React.Suspense fallback={null}>
                <Scenario scenarioName="oneplayer">
                  <OnePlayer className="photoview-primary-media" autoPlay={true} dimensions={dimensions} photo={photo} />
                </Scenario>
              </React.Suspense>
            ) : (
              <>
                <Video
                  aria-live="polite"
                  className="photoview-primary-media"
                  controls={!supportsMediaSrc}
                  controlsRef={videoPlayer}
                  dimensions={dimensions}
                  getPhotoContent={getPhotoContent(downloadPhoto)}
                  loadPlayer={true}
                  muted={false}
                  onEnded={() => (playing.current = false)}
                  onLoadedData={setPhotoRendered}
                  onLoadFailure={setPhotoRendered}
                  onPause={() => (playing.current = false)}
                  onPlay={() => (playing.current = true)}
                  onToggleFullscreen={toggleFullscreen}
                  showControls={supportsMediaSrc ? showControls : "never"}
                  photo={photo}
                  playsInline={true}
                  videoDetails={photo.video}
                />
                <Observer values={{ loaded }}>
                  {({ loaded }) =>
                    !loaded ? (
                      <div className="tile-overlay-spinner absolute-fill flex-row flex-align-center flex-justify-center rounded-6 pointer-events-none">
                        <Spinner size={SpinnerSize.large} />
                      </div>
                    ) : null
                  }
                </Observer>
              </>
            )
          ) : (
            <div className="flex-row relative">
              <Observer values={{ scale }}>
                {({ scale }) => (
                  <Photo
                    aria-live="polite"
                    blocking={true}
                    className="photoview-primary-media"
                    dimensions={dimensions}
                    downloadScale={photo.file?.mimeType === "image/gif" ? "full" : scale}
                    getPhotoContent={_getPhotoContent}
                    onError={setPhotoRendered}
                    onLoad={setPhotoRendered}
                    photo={photo}
                  />
                )}
              </Observer>
            </div>
          )}
          <Observer values={{ transition, transitionType }}>
            {({ transition, transitionType }) => {
              // The transition creates the element then immediately quickly starts its fade out
              if (transition !== undefined) {
                if (transition) {
                  setTransitionTimeout(() => setTransition(false), 100);
                }

                return (
                  <div className="absolute-fill flex-row flex-align-center flex-justify-center pointer-events-none">
                    <div
                      className={css(
                        "video-state-transition flex-row flex-align-center flex-justify-center padding-16 rounded-6",
                        transition === false && "fade-out"
                      )}
                    >
                      {transitionType === "pause" ? <Icons.Pause /> : <Icons.Play />}
                    </div>
                  </div>
                );
              }

              return null;
            }}
          </Observer>
        </div>
      );
    });
  }

  const selectedPhotoId = selection.value.keys().next().value;
  const selectedPhoto = selectedPhotoId ? getItem(selectedPhotoId) : undefined;

  React.useEffect(() => {
    // search results may contain shared content - we will fetch the accessViewpoint for those items
    if (selectedPhoto && selectedPhoto.searchResult && !selectedPhoto.accessViewpoint && sessionContext.migrated) {
      const select = "id,accessViewpoint";
      loadPhotoDetails(eventContext, sessionContext.driveId, selectedPhotoId, { select });
    }
  }, [eventContext, selectedPhoto, selectedPhotoId, sessionContext.driveId, sessionContext.migrated]);

  const livePhoto = selectedPhoto?.photo?.livePhoto;

  // Create a download handler for use of motion photo playback.
  const _downloadPhoto = useFetch(downloadPhoto, {
    blocking: false,
    name: "downloadPhoto",
    telemetryProperties: { scenarioName: "fetchLiveMotionPhoto" }
  });

  // navigating to a previous photo while the current one is playing doesnt reset the observable
  setShowVideo(false);

  useVideoSource({
    loadPlayer: true,
    manifestUrl: motionVideoUrl,
    photoId: selectedPhoto?.id,
    videoElement
  });

  let zoomLevel = 1;
  const { onWheel, onMouseDown, onTouchStart } = usePhotoZoom({
    containerReference: zoomContainerElement,
    imageReference: imageReferenceElement,
    onEventCallback: (dx, dy, scale) => {
      if (zoomContainerElement.current) {
        zoomLevel = scale;
        zoomContainerElement.current.style.transform = "translate3d(" + dx + "px, " + dy + "px, 0) scale(" + scale + ")";
      }
    }
  });

  const { createObjectURL } = useObjectURL();

  React.useMemo(() => {
    if (selectedPhoto && (selectedPhoto.photo?.samsungMotionPhoto || livePhoto)) {
      const options: IDownloadOptions = {
        photoScale: livePhoto ? "manifest" : "full",
        cacheToken: selectedPhoto?.lastModifiedDateTime,
        format: sessionContext.migrated ? "mp4" : undefined
      };

      return _downloadPhoto(selectedPhoto.parentReference?.driveId || sessionContext.driveId, selectedPhoto.id, options)
        .then((blob) => {
          eventContext.dispatchEvent("telemetryAvailable", {
            action: "commandComplete",
            name: "FetchLiveMotionVideo"
          });

          if (livePhoto) {
            setMotionVideoUrl(createObjectURL(blob));
          } else {
            getMotionVideo(blob)
              .then((arrayBuffer) => {
                setMotionVideoUrl(createObjectURL(new Blob([arrayBuffer], { type: "video/mp4" })));
                eventContext.dispatchEvent("telemetryAvailable", {
                  action: "commandComplete",
                  name: "ExtractMotionVideo"
                });
              })
              .catch((error) => {
                setMotionVideoUrl("");
                eventContext.dispatchEvent("telemetryAvailable", {
                  action: "commandComplete",
                  result: {
                    reason: {
                      error: { code: "ExtractMotionVideoFailed", message: error }
                    },
                    status: "rejected"
                  },
                  name: "ExtractMotionVideo"
                });
              });
          }
        })
        .catch((error) => {
          setMotionVideoUrl("");
          eventContext.dispatchEvent("telemetryAvailable", {
            action: "commandComplete",
            result: {
              reason: {
                error: error.error
              },
              status: "rejected"
            },
            name: "FetchLiveMotionVideo"
          });
        });
    }
  }, [_downloadPhoto, createObjectURL, eventContext, livePhoto, selectedPhoto, sessionContext, setMotionVideoUrl]);

  // If we are showing the group preview we will render these buttons.
  const buttons: React.ReactElement[] = [];

  if (groupRange.end > groupRange.start) {
    for (let itemIndex = groupRange.start; itemIndex <= groupRange.end; itemIndex++) {
      const photo = items[itemIndex];
      const { height, width } = fitToDimensions(thumbnailHeight, 0, photo.dimensions.height, photo.dimensions.width);
      const selected = selection.selected(photo.id);

      buttons.push(
        <Button
          ariaLabel={photo.name}
          className={css("photo-view-thumbnail tile-overlay-container relative flex-row flex-noshrink rounded-6", selected && "selected")}
          id={photo.id}
          key={photo.id}
          onClick={(event) => {
            eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "photoViewSelect" });

            // Ensure we don't unselect the last photo, we always require at least one selected.
            if (event.shiftKey) {
              if (selection.selectedCount > 1 || !selection.selected(photo.id)) {
                selection.toggle(photo.id);
              }
            } else {
              selection.select(photo.id);
            }

            event.preventDefault();
          }}
          style={{ height, width }}
          subtle={true}
        >
          <Photo blocking={false} dimensions={{ height, width }} getPhotoContent={getPhotoContent(downloadPhoto)} photo={photo} />
          <TileOverlay className="flex-justify-center photo-view" />
        </Button>
      );
    }
  }

  const canDelete = selectedPhoto?.accessViewpoint?.canDelete ?? true;
  const canDownload = selectedPhoto?.accessViewpoint?.canDownload ?? true;

  // Check if image can be edited
  const editable = photoEditable(sessionContext.driveId, selectedPhoto);

  setDocumentTitle((selectedPhoto && selectedPhoto.name) || "");

  const faceContainer = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const detectedEntities = selectedPhoto?.detectedEntities;
    // If a face entity doesnt have recognizedEntity then it wont show up on the face groupings
    const recognizedFaces = !!detectedEntities?.length && detectedEntities.some((entity: IDetectedEntity) => !!entity.recognizedEntity);

    if (readyToDraw && recognizedFaces && faceContainer.current && selectionDimensions && selection.selectedCount === 1 && selectedPhoto) {
      faceContainer.current.replaceChildren();
      faceContainer.current.style.width = `${selectionDimensions.width}px`;
      faceContainer.current.style.height = `${selectionDimensions.height}px`;

      for (let index = 0; index < detectedEntities.length; index++) {
        const { boundingBoxLeft, boundingBoxTop, boundingBoxWidth, boundingBoxHeight, recognizedEntity } = detectedEntities[index];
        if (recognizedEntity) {
          const rect: IRectangle = {
            left: boundingBoxWidth < boundingBoxHeight ? boundingBoxLeft - (boundingBoxHeight - boundingBoxWidth) / 2 : boundingBoxLeft,
            top: boundingBoxWidth > boundingBoxHeight ? boundingBoxTop - (boundingBoxWidth - boundingBoxHeight) / 2 : boundingBoxTop,
            width: Math.max(boundingBoxHeight, boundingBoxWidth),
            height: Math.max(boundingBoxHeight, boundingBoxWidth)
          };

          const scaledRect = computeCropRect(selectedPhoto.dimensions, rect, selectionDimensions);

          const faceDiv = document.createElement("div");
          faceDiv.style.height = `${scaledRect.height}px`;
          faceDiv.style.width = `${scaledRect.width}px`;
          faceDiv.style.top = `${scaledRect.top}px`;
          faceDiv.style.left = `${scaledRect.left}px`;
          faceDiv.classList.add("cropped-face");
          faceDiv.classList.add("rounded-8");
          faceContainer.current.appendChild(faceDiv);
        }
      }
    }
  }, [readyToDraw, selectedPhoto?.detectedEntities, selectedPhoto, selection.selectedCount, selectionDimensions]);

  return (
    <>
      <div
        className="photo-view standard-view relative flex-column flex-grow overflow-hidden"
        data-theme={sessionContext.embedded ? undefined : "dark"}
        id={getSafeId("photo-view-container")}
        onBlur={onBlur}
        onFocus={onFocus}
        onMouseMove={onMouseMove}
        ref={viewElement}
      >
        {!showFullscreen ? (
          <FocusZone
            direction={FocusZoneDirection.Horizontal}
            focusGroupProps={{ defaultElementId: !shared ? "delete" : "download" }}
            handleTabKey={false}
          >
            <div
              className="photo-view-titlebar flex-row flex-align-center flex-noshrink padding-horizontal-16 padding-vertical-12 overflow-hidden"
              role="menubar"
            >
              <ResponsiveLayout
                containerElement={titlebarElement}
                responsiveSelectors={[
                  getSafeIdSelector("favorite"),
                  getSafeIdSelector("share"),
                  getSafeIdSelector("add-to-album"),
                  getSafeIdSelector("download"),
                  getSafeIdSelector("delete")
                ]}
              >
                {({ responsiveIndex }) => (
                  <div
                    className="photo-view-titlebar-section flex-row flex-align-center rhythm-horizontal-8 overflow-hidden"
                    ref={titlebarElement}
                    role="group"
                  >
                    {!shared && canDelete && (
                      <Button
                        disabled={selection.selectedCount === 0}
                        ariaLabel={DeleteButton}
                        iconProps={{ render: (className: string) => <Icons.Delete className={className} /> }}
                        id="delete"
                        onClick={commandDelete}
                        role="menuitem"
                        subtle={true}
                      />
                    )}
                    {canDownload && (
                      <Button
                        ariaLabel={DownloadButton}
                        disabled={selection.selectedCount === 0}
                        iconProps={{ render: (className: string) => <Icons.Download className={className} /> }}
                        id="download"
                        onClick={commandDownload}
                        role="menuitem"
                        subtle={true}
                      />
                    )}
                    {!sessionContext.embedded && showAlbums && !shared && (
                      <Button
                        ariaLabel={AddToAlbumButton}
                        disabled={selection.selectedCount === 0}
                        iconProps={{ render: (className: string) => <Icons.AddPhotos className={className} /> }}
                        id="add-to-album"
                        onClick={commandAddToAlbum}
                        role="menuitem"
                        subtle={true}
                      />
                    )}
                    {!shared && (
                      <Button
                        ariaLabel={ShareButton}
                        disabled={selection.selectedCount === 0 || (sessionContext.migrated && selection.selectedCount > 1)}
                        iconProps={{ render: (className: string) => <Icons.Share className={className} /> }}
                        id="share"
                        onClick={commandShare}
                        role="menuitem"
                        subtle={true}
                      />
                    )}
                    {!sessionContext.migrated && !shared && sessionContext.accountType !== "business" && (
                      <Button
                        ariaLabel={selectedPhoto?.viewpoint?.isFavorite ? RemoveFavorite : Favorite}
                        disabled={selection.selectedCount !== 1}
                        iconProps={{
                          render: (className: string) => {
                            return selectedPhoto?.viewpoint?.isFavorite ? (
                              <Icons.StarHighlighted className={className} />
                            ) : (
                              <Icons.Star className={className} />
                            );
                          }
                        }}
                        id="favorite"
                        onClick={commandFavorite}
                        role="menuitem"
                        subtle={true}
                      />
                    )}
                    <MenuButton
                      ariaLabel={MoreButton}
                      className={css(responsiveIndex < 3 && "flex-noshrink margin-left-8", responsiveIndex === -1 && "hidden")}
                      contextualMenuProps={{
                        menuProps: {
                          className: "compact-menu",
                          id: "overflow-menu",
                          items: [
                            ...(!sessionContext.migrated && !shared && sessionContext.accountType !== "business"
                              ? [
                                  {
                                    disabled: selection.selectedCount !== 1,
                                    id: "favorite",
                                    iconProps: {
                                      render: (className: string) => {
                                        return selectedPhoto?.viewpoint?.isFavorite ? (
                                          <Icons.StarHighlighted className={className} />
                                        ) : (
                                          <Icons.Star className={className} />
                                        );
                                      }
                                    },
                                    onActivate: /* istanbul ignore next */ (_: IMenuItem, event?: React.KeyboardEvent | React.MouseEvent) =>
                                      commandFavorite(event),
                                    text: selectedPhoto?.viewpoint?.isFavorite ? RemoveFavorite : Favorite
                                  }
                                ]
                              : []),
                            ...(!shared
                              ? [
                                  {
                                    id: "share",
                                    iconProps: { iconName: "Share" },
                                    onActivate: /* istanbul ignore next */ (_: IMenuItem, event?: React.KeyboardEvent | React.MouseEvent) =>
                                      commandShare(event),
                                    text: ShareButton
                                  }
                                ]
                              : []),
                            ...(!sessionContext.embedded && showAlbums && !shared
                              ? [
                                  {
                                    id: "add-to-album",
                                    iconProps: { iconName: "Photo2" },
                                    onActivate: /* istanbul ignore next */ (_: IMenuItem, event?: React.KeyboardEvent | React.MouseEvent) =>
                                      commandAddToAlbum(event),
                                    text: AddToAlbumButton
                                  }
                                ]
                              : []),
                            ...(canDownload
                              ? [
                                  {
                                    disabled: selection.selectedCount > 1,
                                    id: "download",
                                    iconProps: { iconName: "Download" },
                                    onActivate: /* istanbul ignore next */ (_: IMenuItem, event?: React.KeyboardEvent | React.MouseEvent) =>
                                      commandDownload(event),
                                    text: DownloadButton
                                  }
                                ]
                              : []),
                            ...(!shared && canDelete
                              ? [
                                  {
                                    id: "delete",
                                    iconProps: { iconName: "Delete" },
                                    onActivate: /* istanbul ignore next */ (_: IMenuItem, event?: React.KeyboardEvent | React.MouseEvent) =>
                                      commandDelete(event),
                                    text: DeleteButton
                                  }
                                ]
                              : [])
                          ].slice(0, responsiveIndex + 1)
                        }
                      }}
                      hideDropdownIcon={true}
                      iconProps={{ iconName: "MoreVertical" }}
                      role="menuitem"
                      subtle={true}
                    />
                  </div>
                )}
              </ResponsiveLayout>
              <div className="photo-view-titlebar-section flex-row flex-align-center flex-justify-center overflow-hidden" role="group">
                {selection.selectedCount === 1 && editable && (
                  <Button className="outline-button padding-horizontal-40" id="edit" onClick={commandEdit} role="menuitem" text={EditButton} />
                )}
              </div>

              <div className="photo-view-titlebar-section flex-row flex-align-center flex-justify-end rhythm-horizontal-8 overflow-hidden">
                <ResponsiveLayout
                  containerElement={titlebarRightElement}
                  responsiveSelectors={[getSafeIdSelector("selection"), getSafeIdSelector("details"), getSafeIdSelector("full-screen")]}
                >
                  {({ responsiveIndex }) => (
                    <div className="flex-row rhythm-horizontal-8 overflow-hidden" ref={titlebarRightElement} role="group">
                      {selection.selectedCount > 1 && (
                        <Button
                          ariaLabel={`${ClearSelectionButton}, ${format(SelectedItems, { selectionCount: selection.selectedCount })}`}
                          className="outline-button rounded-button margin-right-8"
                          iconProps={{ iconName: "Clear", size: IconSize.small }}
                          id="selection"
                          onClick={clearSelection}
                          role="menuitem"
                          subtle={true}
                          text={
                            layoutStyle === "mobile"
                              ? selection.selectedCount.toString()
                              : format(SelectedItems, { selectionCount: selection.selectedCount })
                          }
                        />
                      )}

                      <Observer values={{ showDetails }}>
                        {({ showDetails }) =>
                          selection.selectedCount === 1 ? (
                            <Button
                              ariaLabel={showDetails ? HidePhotoDetails : ShowPhotoDetails}
                              onClick={(event) => {
                                eventContext.dispatchEvent("telemetryAvailable", {
                                  action: "userAction",
                                  name: showDetails ? "dismissDetails" : "clickDetails"
                                });
                                setShowDetails(!showDetails);
                              }}
                              id="details"
                              iconProps={{ render: (className: string) => <Icons.InformationInverted className={className} /> }}
                              ref={detailsButton}
                              role="menuitem"
                              subtle={true}
                            />
                          ) : null
                        }
                      </Observer>

                      {fullscreenSupported() && (
                        <Button
                          ariaLabel={ToggleFullScreen}
                          iconProps={{ render: (className?: string) => <Icons.EnterFullscreen className={className} /> }}
                          id="full-screen"
                          onClick={(event) => {
                            eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "toggleFullScreen" });
                            toggleFullscreen();
                            event.preventDefault();
                          }}
                          role="menuitem"
                          subtle={true}
                        />
                      )}
                      <MenuButton
                        ariaLabel={MoreButton}
                        className={css(responsiveIndex < 3 && "flex-noshrink margin-left-8", responsiveIndex === -1 && "hidden")}
                        contextualMenuProps={{
                          menuProps: {
                            className: "compact-menu",
                            id: "overflow-menu-right",
                            items: [
                              ...(fullscreenSupported()
                                ? [
                                    {
                                      id: "full-screen",
                                      iconProps: { render: (className?: string) => <Icons.EnterFullscreen className={className} /> },
                                      onActivate: /* istanbul ignore next */ (_: IMenuItem, event?: React.KeyboardEvent | React.MouseEvent) => {
                                        eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "toggleFullScreen" });
                                        toggleFullscreen();
                                        event && event.preventDefault();
                                      },
                                      text: ToggleFullScreen
                                    }
                                  ]
                                : []),
                              ...(selection.selectedCount === 1
                                ? [
                                    {
                                      id: "details",
                                      iconProps: { render: (className: string) => <Icons.InformationInverted className={className} /> },
                                      onActivate: /* istanbul ignore next */ (_: IMenuItem, event?: React.KeyboardEvent | React.MouseEvent) => {
                                        eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "clickDetails" });
                                        setShowDetails(true);
                                        event && event.preventDefault();
                                      },
                                      text: ShowPhotoDetails
                                    }
                                  ]
                                : []),
                              ...(selection.selectedCount > 1
                                ? [
                                    {
                                      id: "selection",
                                      iconProps: { iconName: "Clear", size: IconSize.small },
                                      onActivate: /* istanbul ignore next */ (_: IMenuItem, event?: React.KeyboardEvent | React.MouseEvent) =>
                                        clearSelection(event),
                                      text: `${ClearSelectionButton}, ${format(SelectedItems, { selectionCount: selection.selectedCount })}`
                                    }
                                  ]
                                : [])
                            ]
                          }
                        }}
                        hideDropdownIcon={true}
                        iconProps={{ iconName: "MoreVertical" }}
                        role="menuitem"
                        subtle={true}
                      />
                    </div>
                  )}
                </ResponsiveLayout>
                <Button
                  ariaLabel={CloseButton}
                  iconProps={{ render: (className?: string) => <Icons.Clear className={className} /> }}
                  id="close"
                  onClick={closeView}
                  role="menuitem"
                  subtle={true}
                />
              </div>
            </div>
          </FocusZone>
        ) : null}
        <div className="photo-view-command-overlay absolute-fill flex-row flex-align-start flex-justify-end pointer-events-none">
          {showFullscreen && (
            <div
              className="flex-row flex-align-center padding-horizontal-16 padding-vertical-12 rhythm-horizontal-8 pointer-events-auto"
              role="menubar"
            >
              <Button
                ariaLabel={CloseButton}
                className="photo-view-button"
                iconProps={{ render: (className?: string) => <Icons.Clear className={className} /> }}
                onClick={() => {
                  closeView();
                  toggleFullscreen();
                }}
                role="menuitem"
                subtle={true}
              />
            </div>
          )}
        </div>
        <div className="flex-row flex-grow overflow-hidden" role="main" onKeyDown={onKeyDown}>
          <div
            className={css(
              "photo-view-content relative flex-row flex-wrap flex-grow flex-justify-center user-select-none",
              selection.selectedCount > 1 ? "padding-16 overflow-x-hidden overflow-overlay" : "overflow-hidden"
            )}
            data-name={selectedPhoto?.name}
            id={getSafeId("photo-main")}
            onClick={onClick}
            onDoubleClick={onDblClick}
            ref={containerElement}
            onWheel={(event) => {
              if (!scale.value) {
                setScale(selectedPhoto?.dimensions ?? "full");
                eventContext.dispatchEvent("telemetryAvailable", {
                  action: "userAction",
                  name: "photoZoomIn"
                });
              }
              onWheel(event);
            }}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            style={{ gap: selection.selectedCount > 1 ? 8 : undefined }}
            tabIndex={0}
          >
            {renderedPhotos.length > 0 && (
              <Scenario complete={() => renderedPhotos.length > 0} key={selection.value.values().next().value} scenarioName="renderPrimaryPhoto">
                <div className="photo-container relative flex-row zoom-container" ref={zoomContainerElement} onClick={playMotionPhoto}>
                  {selectedPhoto && (selectedPhoto.photo?.samsungMotionPhoto || livePhoto) && (
                    <div className="motion-badge tile-badge absolute flex-row">
                      <Motion />
                      <span className="hover-badge-text">
                        {selectedPhoto.photo?.samsungMotionPhoto && MotionLabel}
                        {livePhoto && LiveLabel}
                      </span>
                    </div>
                  )}
                  <Observer values={{ showVideo, motionVideoUrl }}>
                    {({ showVideo, motionVideoUrl }) =>
                      motionVideoUrl ? (
                        <div className="flex-row absolute-fill">
                          <video
                            className="motion-video"
                            preload="auto"
                            hidden={!showVideo}
                            ref={videoElement}
                            src={motionVideoUrl}
                            onEnded={() => setShowVideo(false)}
                          >
                            {VideoNotSupported}
                          </video>
                        </div>
                      ) : null
                    }
                  </Observer>

                  <Observer values={{ showDetails }}>
                    {({ showDetails }) => {
                      return (
                        <>
                          <div
                            ref={faceContainer}
                            className={css("photo-view-face-container absolute", (selection.selectedCount !== 1 || !showDetails) && "hidden")}
                          />
                          {!(layoutStyle === "mobile" && showDetails) && renderedPhotos}
                        </>
                      );
                    }}
                  </Observer>
                </div>
              </Scenario>
            )}
            <Observer values={{ showControls }}>
              {({ showControls }) => (
                <div
                  className={css(
                    "photo-view-scroll-overlay absolute-fill flex-row flex-justify-between user-select-none pointer-events-none",
                    showControls === "visible" && "show-controls"
                  )}
                >
                  <div className="photo-view-scroll flex-row flex-align-center padding-horizontal-40 pointer-events-auto">
                    {groupRange.start > 0 && (
                      <div className="flex-row" onDoubleClick={preventDefault}>
                        <Button
                          ariaLabel={PreviousButton}
                          className="photo-view-button rounded-button"
                          iconProps={{ iconName: "ChevronLeftMed", size: IconSize.medium }}
                          onClick={(event) => {
                            eventContext.dispatchEvent("telemetryAvailable", {
                              action: "userAction",
                              mode: "button",
                              name: "showPreviousPhoto"
                            });
                            scrollPhoto(false);
                            event.preventDefault();
                          }}
                          subtle={true}
                          tooltipProps={{}}
                        />
                      </div>
                    )}
                  </div>
                  {groupRange.end > groupRange.start ? (
                    <div className="flex-column flex-align-center flex-justify-end">
                      <Observer values={{ showMore }}>
                        {({ showMore }) => (
                          <span
                            className={css(
                              "photo-group-count body-s font-weight-semibold rounded-16 margin-bottom-24 padding-horizontal-16",
                              showMore && "visible"
                            )}
                          >
                            {format(MorePhotos, { feedId, photoCount: groupRange.end - groupRange.start + 1 })}
                          </span>
                        )}
                      </Observer>
                    </div>
                  ) : null}
                  <div className="photo-view-scroll flex-row flex-align-center padding-horizontal-40 pointer-events-auto">
                    {groupRange.end < items.length - 1 && (
                      <div className="flex-row" onDoubleClick={preventDefault}>
                        <Button
                          ariaLabel={NextButton}
                          className="photo-view-button rounded-button"
                          iconProps={{ iconName: "ChevronRightMed", size: IconSize.medium }}
                          onClick={(event) => {
                            eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", mode: "button", name: "showNextPhoto" });
                            scrollPhoto(true);
                            event.preventDefault();
                          }}
                          subtle={true}
                          tooltipProps={{}}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Observer>
          </div>
          <Observer values={{ showDetails, contentUrl }}>
            {({ showDetails, contentUrl }) => {
              const photo = getItem(Array.from(selection.value)[0]);

              return showDetails && !showFullscreen && photo ? (
                <React.Suspense fallback={null}>
                  <Scenario scenarioName="detailspane">
                    <div
                      className="flex-row"
                      data-testid="details-container"
                      onKeyDown={(event) => {
                        if (!event.defaultPrevented) {
                          if (event.key === "Escape") {
                            detailsButton.current?.focus();
                            setShowDetails(false);
                            event.preventDefault();
                          }
                        }
                      }}
                    >
                      <Details
                        addTag={addTag}
                        className="photo-view-details"
                        contentUrl={contentUrl}
                        deleteTag={deleteTag}
                        downloadPhoto={downloadPhoto}
                        getItem={getDriveItem}
                        loadPhotoDetails={loadPhotoDetails}
                        onDismiss={() => {
                          setShowDetails(false);
                        }}
                        photo={photo}
                        showPhoto={false}
                        updatePhoto={updatePhoto}
                      />
                    </div>
                  </Scenario>
                </React.Suspense>
              ) : null;
            }}
          </Observer>
        </div>
        {groupRange.end > groupRange.start ? (
          <div className="photo-view-strip flex-row flex-align-center flex-noshrink depth-16 user-select-none" onKeyDown={onKeyDown}>
            <FocusZone
              direction={FocusZoneDirection.Horizontal}
              focusGroupProps={{ defaultElementId: items[groupRange.start].id }}
              handleTabKey={false}
            >
              <div className="photo-view-strip-inner flex-row flex-grow flex-justify-center rhythm-horizontal-12" role="list">
                {buttons}
              </div>
            </FocusZone>
          </div>
        ) : null}
      </div>
      <Observer values={{ showAddToAlbum: showAddToAlbumDialog }}>
        {({ showAddToAlbum }) =>
          showAddToAlbum ? (
            <React.Suspense fallback={null}>
              <Scenario scenarioName="addtoalbum">
                <AddToAlbumPanel
                  addToAlbum={addToAlbum}
                  createAlbum={() => setShowCreateAlbumDialog(true)}
                  downloadPhoto={downloadPhoto}
                  getAlbums={(fetch: (url: string, init?: RequestInit) => Promise<Response>, url?: string) => {
                    if (url) {
                      return albumApi.getAlbumsPage(fetch, url).then(filterAlbumsAuto);
                    } else {
                      return albumApi.getAlbums(fetch, sessionContext.driveId).then(filterAlbumsAuto);
                    }
                  }}
                  onDismiss={() => setShowAddToAlbumDialog(false)}
                  photos={Array.from(selection.value).map((id) => getItem(id)!)}
                />
              </Scenario>
            </React.Suspense>
          ) : null
        }
      </Observer>
      <Observer values={{ showCreateAlbumDialog }}>
        {({ showCreateAlbumDialog }) =>
          showCreateAlbumDialog ? (
            <React.Suspense fallback={null}>
              <Scenario scenarioName="createalbumdialog">
                <CreateAlbumDialog
                  createAlbum={createAlbum}
                  downloadPhoto={downloadPhoto}
                  getOldestPhoto={getOldestPhoto}
                  getPhotos={getPhotos}
                  getPhotosPage={getPhotosPage}
                  onDismiss={() => setShowCreateAlbumDialog(false)}
                  photos={Array.from(selection.value).map((id) => getItem(id)!)}
                />
              </Scenario>
            </React.Suspense>
          ) : null
        }
      </Observer>
      <Observer values={{ showDeleteDialog }}>
        {({ showDeleteDialog }) =>
          showDeleteDialog ? (
            <React.Suspense fallback={null}>
              <Scenario scenarioName="deletephotodialog">
                <DeletePhotoDialog
                  deletePhotos={deletePhotos}
                  duplicatePhotoParentName={getDuplicatedPhotosParent(selection, getItem)}
                  onDismiss={() => setShowDeleteDialog(false)}
                  photoIds={getAllSelectedPhotos(selection, getItem).map((photo) => photo.id)}
                />
              </Scenario>
            </React.Suspense>
          ) : null
        }
      </Observer>
      <Observer values={{ showEdit }}>
        {({ showEdit }) =>
          showEdit && selectedPhoto && selectedPhoto.parentReference ? (
            <React.Suspense fallback={null}>
              <Scenario scenarioName="editphotodialog">
                <EditDialog
                  downloadPhoto={downloadPhoto}
                  onDismiss={() => setShowEdit(false)}
                  parentReference={selectedPhoto.parentReference}
                  photo={selectedPhoto}
                />
              </Scenario>
            </React.Suspense>
          ) : null
        }
      </Observer>
      <Observer values={{ showShareDialog }}>
        {({ showShareDialog }) =>
          showShareDialog ? (
            <React.Suspense fallback={null}>
              <Scenario scenarioName="sharedialog">
                <ShareDialog items={Array.from(selection.value).map((id) => getItem(id)!)} onDismiss={() => setShowShareDialog(false)} />
              </Scenario>
            </React.Suspense>
          ) : null
        }
      </Observer>
    </>
  );

  function setPhotoRendered() {
    unstable_batchedUpdates(() => {
      setLoaded(true);
      setPhotoViewLoaded(true);
      setReadyToDraw(true);
    });
  }

  function playMotionPhoto() {
    if (selectedPhoto && (!!selectedPhoto.photo?.samsungMotionPhoto || livePhoto) && zoomLevel === 1) {
      eventContext.dispatchEvent("telemetryAvailable", {
        action: "userAction",
        photoType: livePhoto ? "live" : "motion",
        name: "playLiveMotionVideo"
      });
      setShowVideo(true);
      videoElement.current?.play();
    }
  }

  function clearSelection(event?: React.KeyboardEvent | React.MouseEvent): void {
    eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "photoViewClearSelection" });
    selection.select(items[groupRange.start].id);
    event && event.preventDefault();
  }

  function closeView(event?: React.KeyboardEvent | React.MouseEvent): void {
    navigationContext.navigate(event, "navigateBackButton", { fallback, useBack: true, useReplace: true, telemetryProperties: { navigationSource } });
    event && event.preventDefault();
  }

  function commandAddToAlbum(event?: React.KeyboardEvent | React.MouseEvent): void {
    eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "clickAddToAlbum" });
    setShowAddToAlbumDialog(true);
    event && event.preventDefault();
  }

  function commandDelete(event?: React.KeyboardEvent | React.MouseEvent): void {
    eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "clickDelete" });
    setShowDeleteDialog(true);
    event && event.preventDefault();
  }

  function commandDownload(event?: React.KeyboardEvent | React.MouseEvent): void {
    eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "clickDownload" });
    downloadPhotos(
      eventContext,
      authorizationContext,
      embedContext,
      sessionContext,
      Array.from(selection.value, (photoId) => getItem(photoId)!),
      enableServerZipDownload
    ).catch(handleRejectionWarning);
    event && event.preventDefault();
  }

  function commandShare(event?: React.KeyboardEvent | React.MouseEvent): void {
    eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "clickShare" });
    setShowShareDialog(true);
    event && event.preventDefault();
  }

  function commandEdit(event?: React.KeyboardEvent | React.MouseEvent): void {
    eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "clickEdit" });
    setShowEdit(true);
    event && event.preventDefault();
  }

  function commandFavorite(event?: React.KeyboardEvent | React.MouseEvent): void {
    if (selectedPhoto) {
      eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "clickFavorite" });
      selectedPhoto?.viewpoint?.isFavorite ? removeFavorite(eventContext, [selectedPhoto.id]) : addFavorite(eventContext, [selectedPhoto.id]);
      setCount((count) => count + 1);
      event && event.preventDefault();
    }
  }

  function onClick(event: React.MouseEvent): void {
    if (!event.defaultPrevented) {
      eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "photoViewClick" });

      if (selection.selectedCount === 1 && items[groupRange.start].video) {
        setClickTimeout(togglePlay, 250);
      } else {
        updateShowMore(!showFullscreen);
      }

      event.preventDefault();
    }
  }

  function onDblClick(event: React.MouseEvent): void {
    if (!event.defaultPrevented) {
      eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "photoViewDblClick" });

      clearClickTimeout();

      if (fullscreenSupported()) {
        toggleFullscreen();
      }

      event.preventDefault();
    }
  }

  function onKeyDown(event: React.KeyboardEvent): void {
    if (!event.defaultPrevented) {
      if (event.key === "ArrowRight" && groupRange.end < items.length - 1) {
        eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", mode: "keyboard", name: "showNextPhoto" });
        scrollPhoto(true);
        event.preventDefault();
      } else if (event.key === "ArrowLeft" && groupRange.start > 0) {
        eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", mode: "keyboard", name: "showPreviousPhoto" });
        scrollPhoto(false);
        event.preventDefault();
      } else if (event.key === "Delete") {
        eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", mode: "keyboard", name: "clickDelete" });
        setShowDeleteDialog(true);
        event.preventDefault();
      } else if (event.key === "Escape") {
        closeView(event);
        event.preventDefault();
      }
    }
  }

  function onMouseMove(): void {
    setShowControls("visible");
    setControlsTimeout(() => setShowControls("invisible"), 1000);
    updateShowMore(showFullscreen);
  }

  function scrollPhoto(next: boolean): void {
    let end: number;
    let start: number;

    if (next) {
      if (groupRange.end === items.length - 1) {
        return;
      }

      start = groupRange.end + 1;
      end = start;

      // Ensure we group up the needed photos given the move to the next photo.
      if (autoGroupPhotos) {
        while (end < items.length - 1 && isSimilarPhoto(items[end], items[end + 1])) {
          end++;
        }
      }

      // Page in new photos if we are within 5 of the end with more avilable.
      if (end > items.length - 5 && cursor.nextPage) {
        getItemsFromFeed(cursor.nextPage).catch(handleRejection);
      }
    } else {
      if (groupRange.start === 0) {
        return;
      }

      end = groupRange.start - 1;
      start = end;

      // Ensure we group up the previous photos given the move to the previous photo.
      if (autoGroupPhotos) {
        while (start > 0 && isSimilarPhoto(items[start], items[start - 1])) {
          start--;
        }
      }

      // Page in new photos if we are within 5 of the start with more avilable.
      if (start < 5 && cursor.previousPage) {
        getItemsFromFeed(cursor.previousPage).catch(handleRejection);
      }
    }

    const groupItems = items.slice(start, end + 1);

    // Update the group range and select the first item in the group.
    setGroupRange({ end, start });
    selection.select(items[start].id);

    // Reset the loaded state for the video spinner.
    setLoaded(false);
    setReadyToDraw(false);

    // Since we are moving to a new group, we need to show the more badge when
    // the new group has more than one photo.
    if (groupItems.length > 1) {
      updateShowMore(showFullscreen);
    }

    // The new photo wont be playing.
    playing.current = false;

    // You CANT use react-router here, it will re-render and reload the entire page.
    // We are staying on the same route so we don't really need to render at the moment.
    window.history.replaceState(
      { feedId, photos: groupItems },
      "",
      getViewRoute(groupItems, viewSource, viewToken, fallback, { shared, isVideo: !!groupItems[0].video })
    );
  }

  function toggleFullscreen(): Promise<void> | undefined {
    if (fullscreenSupported()) {
      return document.fullscreenElement ? document.exitFullscreen() : document.body.requestFullscreen();
    }
  }

  function togglePlay(): void {
    const _videoPlayer = videoPlayer.current;
    if (loaded.value && _videoPlayer) {
      eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "toggleVideoPlay" });

      if (playing.current) {
        _videoPlayer.pause();
        setTransitionType("pause");
      } else {
        _videoPlayer.play();
        setTransitionType("play");
      }

      setTransition(true);
    }
  }

  function updateShowMore(showFullScreen: boolean) {
    if (showFullScreen) {
      setShowMore(true);
      setMoreTimeout(() => setShowMore(false), 1500);
    }
  }
}

export default PhotoView;
